import { HeaderLocationData } from '@/components/HeaderEyebrow/types';

const HEADER_LOCATION_EVENT = 'header-location-change';

const snapshotCache = new Map<
  string,
  {
    raw: string | null;
    parsed: HeaderLocationData | null;
  }
>();
let isLoadingLocation =
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/ready/') &&
  new URLSearchParams(window.location.search).has('physical');
/**
 * Generates a unique localStorage key for storing header location data based on the brand code.
 * @param brandCode The brand code to generate the storage key for.
 * @returns A string representing the localStorage key for the given brand code.
 */
function getHeaderLocationStorageKey(brandCode: string) {
  return `header-location:${brandCode}`;
}

/**
 * Parses a raw JSON string from localStorage into a HeaderLocationData object.
 * Returns null if the input is null or if parsing fails.
 * @param raw The raw JSON string to parse. Can be null.
 * @returns The parsed HeaderLocationData object, or null if parsing fails or input is null.
 */
function parseHeaderLocation(raw: string | null): HeaderLocationData | null {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as HeaderLocationData;
  } catch (error) {
    console.error('[headerLocationStorage] Failed to parse stored location:', error);
    return null;
  }
}

/**
 * Reads the header location for a given brand code from localStorage.
 * @param brandCode The brand code to read the location for.
 * @returns The HeaderLocationData object if found and parsed successfully, or null otherwise.
 */
export function readHeaderLocation(brandCode: string): HeaderLocationData | null {
  if (typeof window === 'undefined') return null;

  const storageKey = getHeaderLocationStorageKey(brandCode);
  const raw = window.localStorage.getItem(storageKey);
  return parseHeaderLocation(raw);
}

/**
 * Writes the given header location for a specific brand code to localStorage.
 * @param brandCode The brand code to associate the location with.
 * @param location The HeaderLocationData to store.
 */
export function writeHeaderLocation(brandCode: string, location: HeaderLocationData) {
  if (typeof window === 'undefined') return;

  try {
    const storageKey = getHeaderLocationStorageKey(brandCode);
    const raw = JSON.stringify(location);

    window.localStorage.setItem(storageKey, raw);
    snapshotCache.set(storageKey, {
      raw,
      parsed: location,
    });

    window.dispatchEvent(new CustomEvent(HEADER_LOCATION_EVENT));
  } catch (error) {
    console.error('[headerLocationStorage] Failed to save location:', error);
  }
}

/**
 * Clears the stored header location for a given brand code from localStorage.
 * @param brandCode The brand code to clear the location for.
 */
export function clearHeaderLocation(brandCode: string) {
  if (typeof window === 'undefined') return;

  try {
    const storageKey = getHeaderLocationStorageKey(brandCode);

    window.localStorage.removeItem(storageKey);
    snapshotCache.set(storageKey, {
      raw: null,
      parsed: null,
    });

    window.dispatchEvent(new CustomEvent(HEADER_LOCATION_EVENT));
  } catch (error) {
    console.error('[headerLocationStorage] Failed to clear location:', error);
  }
}

export function setHeaderLocationLoading(isLoading: boolean) {
  if (typeof window === 'undefined') return;
  isLoadingLocation = isLoading;
  window.dispatchEvent(new CustomEvent(HEADER_LOCATION_EVENT));
}

export function getHeaderLocationLoading(): boolean {
  return isLoadingLocation;
}

/**
 * Subscribes to changes in the header location for a specific brand code.
 * The callback will be invoked whenever the location changes, either in the same tab or across different tabs/windows.
 * @param brandCode The brand code to listen for location changes.
 * @param callback The function to call when a location change is detected.
 * @returns A function to unsubscribe from the location changes.
 */
export function subscribeHeaderLocation(brandCode: string, callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const storageKey = getHeaderLocationStorageKey(brandCode);

  const handleStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      callback();
    }
  };

  const handleCustomChange = () => {
    callback();
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(HEADER_LOCATION_EVENT, handleCustomChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(HEADER_LOCATION_EVENT, handleCustomChange);
  };
}

/**
 * Gets a snapshot of the header location for a given brand code, optimized for client-side rendering.
 * This function uses an in-memory cache to avoid unnecessary localStorage reads and JSON parsing.
 * @param brandCode The brand code to get the location snapshot for.
 * @returns The HeaderLocationData object if found and parsed successfully, or null otherwise.
 */
export function getHeaderLocationSnapshot(brandCode: string): HeaderLocationData | null {
  if (typeof window === 'undefined') return null;

  const storageKey = getHeaderLocationStorageKey(brandCode);
  const raw = window.localStorage.getItem(storageKey);
  const cached = snapshotCache.get(storageKey);

  if (cached && cached.raw === raw) {
    return cached.parsed;
  }

  const parsed = parseHeaderLocation(raw);

  snapshotCache.set(storageKey, {
    raw,
    parsed,
  });

  return parsed;
}

/**
 * Gets a snapshot of the header location for server-side rendering.
 * Since localStorage is not available on the server, this function returns null.
 * In a real implementation, you might want to fetch this data from a cookie or an API instead.
 * @returns Always returns null in this implementation.
 */
export function getHeaderLocationServerSnapshot(): HeaderLocationData | null {
  return null;
}
